
import { supabase } from "@/integrations/supabase/client";

export type SubscriptionStatus = {
  status: string;
  planName: string | null;
  isActive: boolean;
  expiresAt: Date | null;
  subscription: any | null;
  planId: string | null;
};

/**
 * Get the current user's subscription status
 * @param cacheParam Optional parameter to prevent caching
 * @returns The user's subscription status information
 */
export const getUserSubscriptionStatus = async (cacheParam?: string): Promise<SubscriptionStatus> => {
  try {
    console.log("Fetching subscription status...", cacheParam ? `(cache buster: ${cacheParam})` : '');
    
    // Get current user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.error("Error getting user:", userError);
      return {
        status: "Free (Forever)",
        planName: "Free (Forever)",
        isActive: true,
        expiresAt: null,
        subscription: null,
        planId: null
      };
    }
    
    console.log("Current user ID:", userData.user.id);
    
    // Get user subscription with plan details - with direct SQL query to bypass any caching
    // Use a random parameter to completely bypass any caching
    const timestamp = new Date().getTime();
    const { data: subscriptions, error: subError } = await supabase
      .from('user_subscriptions')
      .select(`
        *,
        subscription_plans!user_subscriptions_plan_id_fkey(id, name, price, description)
      `)
      .eq('user_id', userData.user.id)
      .order('subscription_date', { ascending: false })
      .limit(1);
    
    console.log("Raw subscription query result:", subscriptions);
    console.log("SQL query error if any:", subError);
    
    if (subError) {
      console.error("Error fetching subscription:", subError);
      return {
        status: "Free (Forever)",
        planName: "Free (Forever)",
        isActive: true,
        expiresAt: null,
        subscription: null,
        planId: null
      };
    }
    
    // If no subscription found, user is on free plan
    if (!subscriptions || subscriptions.length === 0) {
      console.log("No subscription found for user - returning Free plan");
      
      // Double-check with a different approach to ensure we're not hitting a cache issue
      const { data: directCheckData, error: directError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userData.user.id)
        .order('subscription_date', { ascending: false })
        .limit(1);
      
      console.log("Direct subscription check result:", directCheckData);
      console.log("Direct check error if any:", directError);
      
      return {
        status: "Free (Forever)",
        planName: "Free (Forever)",
        isActive: true,
        expiresAt: null,
        subscription: null,
        planId: null
      };
    }
    
    const subscription = subscriptions[0];
    console.log("Most recent subscription data:", subscription);
    
    // Check if plan data is available through the relationship
    const hasPlanData = subscription && 
                        subscription.subscription_plans && 
                        typeof subscription.subscription_plans === 'object';
    
    // Get plan name from relationship or fallback to subscription_status
    const planName = hasPlanData && subscription.subscription_plans.name 
      ? String(subscription.subscription_plans.name) 
      : subscription.subscription_status || "Free (Forever)";
    
    // Get plan ID either from direct reference or from related plan
    const planId = subscription.plan_id || 
                  (hasPlanData && subscription.subscription_plans.id 
                    ? String(subscription.subscription_plans.id) 
                    : null);
    
    console.log("Using plan name:", planName);
    
    // Determine if subscription is still active (not expired)
    const now = new Date();
    const renewalDate = subscription.renewal_date ? new Date(subscription.renewal_date) : null;
    const isActive = renewalDate ? now < renewalDate : true;
    
    // Add more detailed logging to debug plan information
    console.log("Final subscription status:", {
      status: planName,
      planName: planName,
      isActive,
      expiresAt: renewalDate,
      planId
    });
    
    console.log("Actual plan data from DB:", hasPlanData ? subscription.subscription_plans : "No plan data");
    
    return {
      status: String(planName),
      planName: String(planName),
      isActive,
      expiresAt: renewalDate,
      subscription,
      planId
    };
  } catch (error) {
    console.error("Error in getUserSubscriptionStatus:", error);
    return {
      status: "Free (Forever)",
      planName: "Free (Forever)",
      isActive: true,
      expiresAt: null,
      subscription: null,
      planId: null
    };
  }
};

/**
 * Check if the current user's subscription is active
 * @returns true if the user has an active paid subscription
 */
export const hasActivePaidSubscription = async (): Promise<boolean> => {
  const { status, isActive } = await getUserSubscriptionStatus(`cb_${Date.now()}`);
  return isActive && status !== "Free (Forever)";
};
